import { useContext, useEffect, useState } from "react";
import { OAuthError, patchData, postData } from "../backend";
import { PlusCircleIcon, CalendarIcon, PencilAltIcon, TrashIcon, ChevronRightIcon, PencilIcon, CheckIcon, UserCircleIcon, XIcon, ChevronDownIcon, InformationCircleIcon, CogIcon, DuplicateIcon } from "@heroicons/react/outline";
import { Document, SignatureField, SignatureConfig } from "../interfaces";
import { Combobox } from "@headlessui/react";
import { WorkflowContext, useModal } from "../contexts";
import { SourceField } from "../interfaces";
import { ICON_MAP, ORDINAL_MAP, RECIPIENT_COLOUR_MAP, SINGATURE_FIELD_SIZE_MAP } from "../utils";
import WorkflowSignaturesConfigModal from "./WorkflowSignaturesConfigModal";
import OAuthModalHubspot from "./OAuthModalHubspot";
import OAuthModalXero from "./OAuthModalXero";
import OAuthModal from "./OAuthModal";
import ErrorModal from "./ErrorModal";
import { useTranslation } from "react-i18next";
import HeroIcon from "./HeroIcon";
import CopyToClipboard from "react-copy-to-clipboard";
import SmallTextInputField from "./SmallTextInputField";
import WorkflowModalHubspotPropertiesCaptureList from "./WorkflowModalHubspotPropertiesCaptureList";

interface Props {
  document?: Document;
  recipientNumber: number;
  signatureConfig: SignatureConfig
  updateSignatureConfig: (updates: any) => void;
  hideFields?: boolean;
  insertSignatureField?: (field: SignatureField, recipient: number) => void;
  buttonTextCopy?: boolean;
}

function WorkflowSignatureConfigItem(props: Props) {
  const { document, recipientNumber, signatureConfig, updateSignatureConfig } = props;
  const { t } = useTranslation();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow } = workflowContext;

  const { openModal } = useModal();

  const recipientEmailKey = "recipientEmail" + recipientNumber;
  const [textColor, bgColor] = RECIPIENT_COLOUR_MAP[recipientNumber];

  const [copiedTag, setCopiedTag] = useState<string>();
  const [openCaptureField, setOpenCaptureField] = useState<string>();

  const [currentCapturePlaceholder, setCurrentCapturePlaceholder] = useState<string>("");
  const [currentCaptureIsRequired, setCurrentCaptureIsRequired] = useState<boolean>(false);

  // @ts-ignore
  const [email, setEmail] = useState<string>(signatureConfig[recipientEmailKey]);

  const [isOpened, setIsOpened] = useState(!!document); // Only open when there is a document present
  const [clickedField, setClickedField] = useState<SignatureField | "TEXT_REQUIRED" | "CHECKBOX_REQUIRED">();

  useEffect(() => {
    if (!openCaptureField)
      return;

    const field = workflow.source?.sourceFields.find(f => f.id === openCaptureField);

    setCurrentCapturePlaceholder(field?.capturePlaceholder || "");
    setCurrentCaptureIsRequired(!!field?.captureIsRequired);

  }, [openCaptureField, workflow.source?.sourceFields]);

  async function handleCopySignatureField(field: SignatureField, recipient: number, required: boolean = false) {
    if (clickedField)
      return; // Prevent action while a field is already being inserted

    setClickedField(field + (required ? "_REQUIRED" : "") as SignatureField);

    const type = field.toLocaleLowerCase();
    const flag = required ? "-required" : "";
    const path = `https://static.portant.co/field-${type}${flag}-u${recipient}.png`;

    try {
      const img = await fetch(path);
      const imgBlob = await img.blob();

      const imageUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.readAsDataURL(imgBlob);
      });

      const [width, height] = SINGATURE_FIELD_SIZE_MAP[field];
      const htmlBlob = `<span style="border:none;display:inline-block;overflow:hidden;width:${width}px;height:${height}px;"><img src=${imageUrl} width="${width}" height="${height}" style="margin-left:0px;margin-top:0px;"></span>`

      navigator.clipboard.write([
        new ClipboardItem({ 'text/html': htmlBlob })
      ]);
    } catch (error) {
      console.error(error);
    }

    setTimeout(() => setClickedField(undefined), 1000);
  }

  async function handleInsertSignatureField(field: SignatureField, recipient: number, required: boolean = false) {
    if (clickedField)
      return; // Prevent action while a field is already being inserted

    if (props.insertSignatureField)
      return props.insertSignatureField(field, recipient);

    const type = field.toLocaleLowerCase();

    try {
      setClickedField(field + (required ? "_REQUIRED" : "") as SignatureField);
      await postData(`/workflows/documents/${document!.id}/add-signature-field/`, { type, recipient, required })
    } catch (error) {
      if (error instanceof OAuthError)
        if (error.oauthUrl.includes("app.hubspot"))
          openModal(<OAuthModalHubspot oauthUrl={error.oauthUrl} />);
        else if (error.oauthUrl.includes("xero"))
          openModal(<OAuthModalXero oauthUrl={error.oauthUrl} />);
        else
          openModal(<OAuthModal oauthUrl={error.oauthUrl} />);
      else
        openModal(<ErrorModal details={error} />);
    } finally {
      setClickedField(undefined)
    }
  }

  function handleUpdateEmail(newEmail?: string) {
    setEmail(newEmail ?? email);
    updateSignatureConfig({ [recipientEmailKey]: newEmail ?? email });
  }

  function handleRemoveRecipient() {
    const numRecipients = Math.max(signatureConfig!.numRecipients - 1, 1);
    updateSignatureConfig({ numRecipients, [recipientEmailKey]: "" });
  }

  function handleCopyTag(tag: string) {
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(undefined), 2000);
  }

  function updateField(fieldId: string, updates: Partial<SourceField>) {
    patchData(`/workflows/source-fields/${fieldId}/`, updates)
      .catch(console.error);
  }

  const filteredContacts = workflow.source?.hubspotConfig?.hubspotContactLabels.filter((label: string) => label.toLowerCase().includes(email.toLowerCase())) ?? [];
  const filteredFields = workflow.source?.sourceFields.filter((sourceField: SourceField) => {
    return sourceField.fieldName.toLowerCase().includes(email.toLowerCase().replaceAll("{", "").replaceAll("}", ""))
  }) ?? [];

  if (!workflow?.signatureConfig)
    return null

  const hubspotContactLabel = email.match(/{{__hscontact_(.+?)(?::(.+))?}}/);

  return (
    <div className="border border-gray-400 rounded">
      <div className={`flex flex-col gap-1 border-b px-2 py-1 pb-2 bg-opacity-10 rounded ${bgColor}`}>
        <div className="flex items-center w-full">
          <div className={`flex items-center cursor-pointer ${textColor}`} onClick={() => setIsOpened(f => !f)}>
            {(!!document && false) &&
              <ChevronRightIcon className="w-6 h-6 mr-2" style={{ "rotate": isOpened ? "90deg" : "0deg" }} />
            }
            <div className={`font-gilroy font-semibold text-sm`}>
              {ORDINAL_MAP[recipientNumber]} {t("Recipient")}
            </div>
          </div>
          {(document && recipientNumber > 1 && recipientNumber === signatureConfig.numRecipients) &&
            <TrashIcon className="w-4 h-4 ml-auto text-gray-600 hover:text-red cursor-pointer" onClick={handleRemoveRecipient} />
          }
          {(document && recipientNumber === 1) &&
            <div className="relative group ml-auto">
              <InformationCircleIcon className="w-4 h-4 ml-auto text-gray-600" />
              <div className="tooltip group-hover:visible right-2 top-4 text-wrap w-60 rounded-tr-none">
                {t("Signable fields will be inserted at the")} <b className="whitespace-nowrap">{t("end of the document")}</b> {t("and can be moved to any location.")}
              </div>
            </div>
          }
        </div>
        <div className="relative w-full">
          <Combobox value={email} onChange={handleUpdateEmail}>
            {hubspotContactLabel
              ? (
                <div className="border-2 rounded py-1 px-2 w-full outline-none text-sm flex items-center bg-white">
                  <UserCircleIcon className="h-4 mr-2 text-[#6e839a]" />
                  <div className="text-sm font-semibold text-gray-600">{hubspotContactLabel[2] || t("Contact")}</div>
                  <Combobox.Button className="absolute right-0 top-0 bottom-0 flex items-center pr-2" onClick={() => handleUpdateEmail("")}>
                    <XIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </Combobox.Button>
                </div>
              ) : (
                <div className="relative">
                  <Combobox.Input className="border-2 rounded py-1 px-2 w-full pr-8 outline-none text-sm" placeholder={t("Enter or select recipient email")} onChange={(event) => setEmail(event.target.value)} />
                  <Combobox.Button className="absolute right-0 top-0 bottom-0 flex items-center pr-2" onClick={() => handleUpdateEmail("")}>
                    {email.length === 0
                      ? <ChevronDownIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                      : <XIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                    }
                  </Combobox.Button>
                </div>
              )
            }
            <Combobox.Options className="absolute top-9 w-full z-40 flex flex-col max-h-52 bg-white border rounded shadow overflow-y-auto">
              <>
                {!!workflow.source?.hubspotConfig &&
                  <>
                    {email.length === 0 &&
                      <Combobox.Option value={`{{__hscontact_${recipientNumber}}}`} className="flex items-center text-sm text-left border-b border-gray-200 last:border-none p-2 hover:bg-blue hover:bg-opacity-10 cursor-pointer group flex-shrink-0">
                        <UserCircleIcon className="h-8 mr-2 text-[#6e839a]" />
                        <div className="flex flex-col items-start">
                          <div className="text-xs font-semibold text-gray-600">{t("HubSpot Contact")}</div>
                        </div>
                      </Combobox.Option>
                    }
                    {filteredContacts.map((label) => (
                      <Combobox.Option key={label} value={`{{__hscontact_${label}}}`} className="flex items-center text-sm text-left border-b border-gray-200 last:border-none p-2 hover:bg-blue hover:bg-opacity-10 cursor-pointer group flex-shrink-0">
                        <UserCircleIcon className="h-8 mr-2 text-[#6e839a]" />
                        <div className="flex flex-col items-start">
                          <div className="text-xs font-semibold text-gray-400">{t("HubSpot Contact")}</div>
                          <div className="flex items-center gap-1 text-sm font-semibold font-gilroy text-gray-600">
                            {t("Labeled")}:
                            <div className="text-sm bg-gray-100 rounded px-2 font-semibold font-gilroy text-gray-600">
                              {label.split(":")[1]}
                            </div>
                          </div>
                        </div>
                      </Combobox.Option>
                    ))}
                  </>
                }
                {!workflow.source?.hubspotConfig &&
                  <>
                    {filteredFields.map((field) => (
                      <Combobox.Option key={field.id} value={`{{${field.fieldName}}}`} className="flex items-center justify-between text-left text-sm border-b border-gray-200 last:border-none px-2 pt-2 pb-1 hover:bg-blue hover:bg-opacity-10 cursor-pointer group flex-shrink-0">
                        {field.fieldName}
                      </Combobox.Option>
                    ))}
                  </>
                }
                {email.length > 0 &&
                  <Combobox.Option value={email} className="flex items-center justify-between text-left text-sm border-b text-gray-400 border-gray-200 last:border-none px-2 pt-2 pb-1 hover:bg-blue hover:bg-opacity-10 cursor-pointer group flex-shrink-0">
                    {email}
                    <div className="hidden group-first:flex text-xs bg-blue-50 roudned px-1 font-semibold font-gilroy text-gray-600">
                      Enter
                    </div>
                  </Combobox.Option>
                }
              </>
            </Combobox.Options>
          </Combobox>
        </div>
      </div>

      <div className={`flex flex-col ${(document && isOpened && !props.hideFields) ? "block" : "hidden"}`}>
        <div className="flex items-center text-sm border-b px-2 py-1">
          <PencilAltIcon className="w-4 h-4 mr-2" />
          <div className="font-semibold whitespace-nowrap py-2">
            {t("Signature")}
          </div>
          {props.buttonTextCopy
            ? (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "SIGNATURE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("SIGNATURE", recipientNumber)}>
                {clickedField === "SIGNATURE" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
              </button>
            )
            : (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "SIGNATURE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("SIGNATURE", recipientNumber)}>
                {clickedField === "SIGNATURE" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
              </button>
            )
          }
        </div>

        <div className="flex items-center text-sm border-b px-2 py-1">
          <div className="w-4 h-4 mr-2 font-gilroy text-sm underline">
            AB
          </div>
          <div className="font-semibold whitespace-nowrap py-2">
            {t("Initials")}
          </div>
          {props.buttonTextCopy
            ? (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "INITIALS" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("INITIALS", recipientNumber)}>
                {clickedField === "INITIALS" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
              </button>
            )
            : (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "INITIALS" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("INITIALS", recipientNumber)}>
                {clickedField === "INITIALS" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
              </button>
            )
          }
        </div>

        <div className="flex items-center text-sm border-b px-2 py-1">
          <div className="w-4 h-4 mr-2 font-gilroy text-sm underline">
            Tt
          </div>
          <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
            {t("Text")}
            <div className="text-xs text-gray-400 font-semibold">
              {t("Optional")}
            </div>
          </div>
          {props.buttonTextCopy
            ? (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXT" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("TEXT", recipientNumber)}>
                {clickedField === "TEXT" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
              </button>
            )
            : (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXT" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("TEXT", recipientNumber)}>
                {clickedField === "TEXT" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
              </button>
            )
          }
        </div>

        <div className="flex items-center text-sm border-b px-2 py-1">
          <div className="w-4 h-4 mr-2 font-gilroy text-sm underline decoration-red">
            Tt
          </div>
          <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
            {t("Text")}
            <div className="text-xs text-red/50 font-semibold">
              {t("Required")}
            </div>
          </div>
          {props.buttonTextCopy
            ? (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXT_REQUIRED" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("TEXT", recipientNumber, true)}>
                {clickedField === "TEXT_REQUIRED" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
              </button>
            )
            : (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXT_REQUIRED" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("TEXT", recipientNumber, true)}>
                {clickedField === "TEXT_REQUIRED" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
              </button>
            )
          }
        </div>

        <div className="flex items-center text-sm border-b px-2 py-1 last:border-b-0">
          <CheckIcon className="w-4 h-4 mr-2 border border-black rounded" />
          <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
            {t("Checkbox")}
            <div className="text-xs text-gray-400 font-semibold">
              {t("Optional")}
            </div>
          </div>
          {props.buttonTextCopy
            ? (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "CHECKBOX" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("CHECKBOX", recipientNumber)}>
                {clickedField === "CHECKBOX" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
              </button>
            )
            : (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "CHECKBOX" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("CHECKBOX", recipientNumber)}>
                {clickedField === "CHECKBOX" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
              </button>
            )
          }
        </div>

        <div className="flex items-center text-sm border-b px-2 py-1 last:border-b-0">
          <CheckIcon className="w-4 h-4 mr-2 border border-red rounded" />
          <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
            {t("Checkbox")}
            <div className="text-xs text-red/50 font-semibold">
              {t("Required")}
            </div>
          </div>
          {props.buttonTextCopy
            ? (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "CHECKBOX_REQUIRED" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("CHECKBOX", recipientNumber, true)}>
                {clickedField === "CHECKBOX_REQUIRED" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
              </button>
            )
            : (
              <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "CHECKBOX_REQUIRED" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("CHECKBOX", recipientNumber, true)}>
                {clickedField === "CHECKBOX_REQUIRED" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
              </button>
            )
          }
        </div>

        <div className="flex items-center text-sm border-b px-2 py-1">
          <div className="flex flex-col">
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-2" />
              <div className="font-semibold whitespace-nowrap py-2">
                {t("Date")}
              </div>
              <div className="text-xs ml-auto flex items-start group cursor-pointer hover:text-blue hover:underline" onClick={() => openModal(<WorkflowSignaturesConfigModal {...workflowContext} />)}>
                {workflow.signatureConfig!.dateFormat || workflow.owner?.dateFormat}
                <div className="ml-1">
                  {(signatureConfig.dateTimeFormat || workflow.owner?.timeFormat) === "HOUR_12" && "12Hr"}
                  {(signatureConfig.dateTimeFormat || workflow.owner?.timeFormat) === "HOUR_24" && "24Hr"}
                </div>
                <PencilIcon className="opacity-0 group-hover:opacity-100 w-3 h-3 ml-1" />
              </div>
              {props.buttonTextCopy
                ? (
                  <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "DATE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleCopySignatureField("DATE", recipientNumber)}>
                    {clickedField === "DATE" ? t("Copied") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
                  </button>
                )
                : (
                  <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "DATE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("DATE", recipientNumber)}>
                    {clickedField === "DATE" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1" /> {t("Insert")}</>}
                  </button>
                )
              }
            </div>
            <div className="text-xs mt-1">{t("Automatically populated with the current date of when the recipient completes the document.")}</div>
          </div>
        </div>

        {/* @ts-ignore-next-line */}
        {(workflow.source?.sourceType === "HUBSPOT" && workflow.signatureConfig["recipientEmail" + props.recipientNumber] && document?.documentType !== "PDF_FILLABLE") &&
          <>
            <div className="flex items-center text-sm px-2 py-1 bg-gray-100 font-semibold font-gilroy text-gray-600">
              HubSpot Capture Fields
              <div className="ml-auto mr-2 relative group">
                <InformationCircleIcon className={`h-4 w-4 text-gray-600 cursor-default`} />
                <div className="tooltip group-hover:visible right-2 top-4 text-wrap w-60 rounded-tr-none">
                  {t("Insert fields into the signable document to capture data that is written to the contact in HubSpot.")}
                </div>
              </div>
              <CogIcon className="h-4 w-4 mr-2 text-gray-600 cursor-pointer" onClick={() => openModal(<WorkflowModalHubspotPropertiesCaptureList workflow={workflow} refreshWorkflow={mutateWorkflow} onClose={() => { mutateWorkflow(); }} />)} />
            </div>

            {workflow.source?.sourceFields.filter(field => workflow?.source?.hubspotConfig?.hubspotCaptureProperties.includes(field.fieldId))
              .map((field: SourceField, index: number) => (
                <div key={field.id} className="flex flex-col justify-center py-1 px-2 text-sm border-b last:border-b-0 w-full">
                  <div className="flex w-full items-center gap-1">
                    <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transform transition-all cursor-pointer ${openCaptureField === field.id ? "rotate-0" : "-rotate-90"}`} onClick={() => setOpenCaptureField(openCaptureField === field.id ? undefined : field.id)} />
                    <div className="font-semibold pt-2 pb-1 truncate">
                      {field.fieldName}
                    </div>
                    {field.captureIsRequired &&
                      <span className="text-red -ml-1">*</span>
                    }
                    <div className="ml-auto relative group">
                      <HeroIcon icon={ICON_MAP[field.fieldType][0]} className={`w-4 h-4 flex-shrink-0 cursor-pointer ${!field.customMapping ? "text-gray-600" : "text-blue"}`} />
                      <div className="hidden group-hover:flex absolute -top-1 right-6 bg-black text-white text-xs rounded-sm z-10 px-2 py-1 whitespace-nowrap select-none">
                        <svg height="8px" width="4px" xmlns="http://www.w3.org/2000/svg" className="absolute text-black" style={{ right: "-4px", top: "calc(100% / 2 - 4px)" }}>
                          <polygon points="0,0 4,4 0,8" fill="currentColor" />
                        </svg>
                        {ICON_MAP[field.fieldType][1]}
                      </div>
                    </div>
                    <CopyToClipboard text={`[[${recipientNumber}: ${field.fieldName}]]`} onCopy={() => handleCopyTag(field.fieldId)}>
                      <button className={`ml-2 btn btn-white px-2 py-1 w-20 flex-shrink-0 ${copiedTag === field.fieldId ? "bg-blue text-white" : "bg-white text-blue"}`} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        {copiedTag === field.fieldId ? t("Copied!") : <><DuplicateIcon className="w-4 h-4 mr-1" /> {t("Copy")}</>}
                      </button>
                    </CopyToClipboard>
                  </div>
                  {openCaptureField === field.id &&
                    <div className="flex flex-col gap-1 mt-1">
                      {field.fieldType !== "CHECKBOX" &&
                        <SmallTextInputField className="mb-2" label={t("Placeholder")} value={currentCapturePlaceholder} placeholder={"Ab"} onChange={setCurrentCapturePlaceholder} onEnter={() => updateField(field.id, { capturePlaceholder: currentCapturePlaceholder })} />
                      }
                      <label className="flex items-center gap-1 font-semibold text-gray-600 text-sm">
                        <input type="checkbox" className="mb-0.5" checked={currentCaptureIsRequired} onChange={e => { setCurrentCaptureIsRequired(e.currentTarget.checked); updateField(field.id, { captureIsRequired: e.currentTarget.checked }) }} />
                        {field.fieldType !== "CHECKBOX"
                          ? t("Require recipient to fill this field")
                          : t("Require recipient to check this field")
                        }
                      </label>
                    </div>
                  }
                </div>
              ))}

            <div className="flex justify-center py-2 px-2 text-sm border-b last:border-b-0 items-center gap-1 text-blue">
              <PlusCircleIcon className="w-4 h-4" />
              <div className="flex items-center gap-1 font-semibold font-gilroy cursor-pointer hover:underline" onClick={() => openModal(<WorkflowModalHubspotPropertiesCaptureList workflow={workflow} refreshWorkflow={mutateWorkflow} onClose={() => { mutateWorkflow(); }} />)}>
                {t("Select HubSpot Fields")}
              </div>
            </div>
          </>
        }
      </div>
    </div>
  );
}

export default WorkflowSignatureConfigItem;
